/**
 * `PasswordEntry` — the entry-screen form for the Secret Chat SPA.
 *
 * Responsibilities (requirements coverage R1.1, R1.4, R1.5, R7.2, R8.2,
 * R9.1, R9.2, R9.3):
 *   - Show a password input, a nickname input, a submit button and short
 *     guidance copy so non-technical visitors understand the flow (R1.1,
 *     R9.1).
 *   - Hash the password client-side via the injected `sha256Hex` helper so
 *     the plaintext never leaves the browser (R1.3, R8.1 — enforced by the
 *     injected helper, not by this component, but this component is the
 *     entry point that triggers it).
 *   - When the password input loses focus, derive the roomId and consult
 *     `storage.loadNickname(roomId)` to prefill the nickname for returning
 *     users (R7.2, R9.2). Never overwrite a value the user has already
 *     typed.
 *   - Pre-validate empty / over-long inputs on the client so trivial
 *     mistakes don't burn an API round-trip (R1.4). The server is still
 *     the authority — its 400 `invalidInput` response is surfaced verbatim.
 *   - Map every `ChatApiError.kind` to a localized message that names the
 *     failure mode (R1.5, R9.3). The errors live next to a `role="alert"`
 *     region so assistive tech announces them automatically.
 *   - Guard against double-submit by disabling the button (and short-
 *     circuiting submit handlers) while a request is in flight.
 *   - On success, persist the nickname (`storage.saveNickname`) and hand
 *     off `{ roomId, nickname, joinOutput }` to the parent (`onJoined`) —
 *     the routing decision (which screen comes next) lives one layer up
 *     in `App.tsx` (task 7.1).
 *
 * Dependency direction (`design.md` §Dependency Direction):
 *   This component depends only on the `ChatApi` interface and on injected
 *   `sha256Hex` / `storage` functions. It does *not* import the concrete
 *   `createChatApi` factory, the global `crypto` API, or `localStorage`
 *   directly — every collaborator is mockable for tests and swappable in
 *   `App.tsx` for production wiring.
 *
 * Test strategy: see `PasswordEntry.test.tsx`. The component is exercised
 * via `@testing-library/react`'s `render` + `fireEvent` (the project does
 * not depend on `@testing-library/user-event`).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChangeEvent, FormEvent, JSX } from 'react';

import { HamburgerMenu } from './HamburgerMenu';

import type {
  ChatApi,
  ChatApiError,
  RoomJoinOutput,
} from '../api/chatApi';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * Result handed to the parent on a successful join. We expose both the
 * derived `roomId` (so the parent can use it as a hook dependency without
 * recomputing the SHA-256 hash) and the full server response so screens
 * downstream can read `participants`, `readReceipts`, and `lastSeq` without
 * a second round-trip.
 */
export interface PasswordEntryJoinSuccess {
  readonly roomId: string;
  readonly nickname: string;
  readonly joinOutput: RoomJoinOutput;
}

/**
 * Minimal storage contract. We intentionally do not depend on the concrete
 * `web/src/lib/storage.ts` module directly — production code passes those
 * functions in, tests pass spies.
 */
export interface NicknameStorage {
  loadNickname(roomId: string): string | null;
  saveNickname(roomId: string, nickname: string): void;
}

export interface PasswordEntryProps {
  /** REST surface used to call `POST /api/rooms/{roomId}/join`. */
  readonly chatApi: ChatApi;
  /** Web Crypto SHA-256 helper (`web/src/lib/crypto.ts`). Injected for testability. */
  readonly sha256Hex: (text: string) => Promise<string>;
  /** localStorage adapter (`web/src/lib/storage.ts`). Injected for testability. */
  readonly storage: NicknameStorage;
  /** Called once the join succeeds and the nickname has been persisted. */
  readonly onJoined: (success: PasswordEntryJoinSuccess) => void;
  /** Navigate to login screen for room management. */
  readonly onGoToLogin: () => void;
  /**
   * 内部のハンバーガーメニューを表示するか。`LandingPage` に埋め込むときは
   * ページ右上にメニューを置くため `false` を渡し、二重表示を防ぐ。
   * 省略時は `true`（スタンドアロン表示時の従来挙動）。
   */
  readonly showMenu?: boolean;
  /**
   * `LandingPage` のヒーロー内に溶け込ませる埋め込みモード。`true` のとき
   * スタンドアロン用の全画面ラッパー（theme-stage / 背景 blob）を描画せず、
   * フォーム本体（section）だけを返す。省略時は `false`（従来の全画面表示）。
   */
  readonly embedded?: boolean;
}

// ---------------------------------------------------------------------------
// Validation constants
// ---------------------------------------------------------------------------

/**
 * Mirror of the server's nickname max length (`api/src/domain/validation.ts`).
 * Kept as a local constant rather than imported so the entry screen stays
 * decoupled from the domain module; if the server bumps the cap, the
 * mismatch surfaces as a server-side 400 which we already render.
 */
const NICKNAME_MAX_LENGTH = 32;

// ---------------------------------------------------------------------------
// Error message mapping
// ---------------------------------------------------------------------------

/**
 * Translate a `ChatApiError` into a user-facing Japanese sentence. We
 * intentionally avoid surfacing raw status codes or stack traces — the
 * `console.error` line in the catch path is where diagnostic detail goes.
 */
function describeError(error: ChatApiError): string {
  switch (error.kind) {
    case 'invalidInput':
      return '入力内容を確認してください。';
    case 'roomFull':
      return 'このルームは満員です。';
    case 'rateLimited':
      return `試行回数が多すぎます。${error.retryAfterSeconds}秒後にもう一度お試しください。`;
    case 'networkError':
      return 'ネットワークエラーです。接続を確認してから再度お試しください。';
    case 'serverError':
      return 'サーバエラーが発生しました。しばらくしてからもう一度お試しください。';
    case 'concurrencyConflict':
      return '一時的な競合が発生しました。再度お試しください。';
    case 'notJoined':
    case 'roomNotFound':
      // join では本来発生しない応答だが、防御的に user 向け表現を返す。
      return 'ルームを開けませんでした。再度お試しください。';
    case 'guestLimitReached':
      // join 経路では発生しないが、ChatApiError の網羅性のために形式上対応。
      return 'メッセージ送信数の上限に達しています。';
    case 'bannedWord':
      // join 経路では発生しないが、ChatApiError の網羅性のために形式上対応。
      return 'NGワードが含まれているため処理できませんでした。';
    default: {
      // Exhaustiveness guard: if a new `kind` is added to `ChatApiError`
      // and we forget to handle it here, the type system fails at compile
      // time. The runtime fallback is a generic message.
      const _exhaustive: never = error;
      void _exhaustive;
      return '予期せぬエラーが発生しました。';
    }
  }
}

// ---------------------------------------------------------------------------
// Client-side validation
// ---------------------------------------------------------------------------

/**
 * Cheap pre-check before any network or crypto work. Returns a Japanese
 * error string when the form is unfit to submit, or `null` when ready.
 *
 * This is *not* a substitute for server validation — it just keeps the UI
 * snappy for obvious mistakes (empty inputs, glaringly long nicknames).
 */
function validateInputs(password: string, nickname: string): string | null {
  if (password.length === 0) {
    return '合言葉を入力してください。';
  }
  if (nickname.length === 0) {
    return 'ニックネームを入力してください。';
  }
  if (nickname.length > NICKNAME_MAX_LENGTH) {
    return `ニックネームは${NICKNAME_MAX_LENGTH}文字以内で入力してください。`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PasswordEntry(props: PasswordEntryProps): JSX.Element {
  const { chatApi, sha256Hex, storage, onJoined, onGoToLogin, showMenu = true, embedded = false } = props;

  // Form state — kept as primitive strings so re-renders are cheap and
  // controlled-input semantics are obvious.
  const [password, setPassword] = useState('');
  const [nickname, setNickname] = useState('');

  // `nicknameTouched` tracks whether the user has typed into the nickname
  // field. It's the flag that lets the prefill effect respect the user's
  // intent: once they've touched the field, storage never overwrites their
  // value (R9.2 implementation note).
  const [nicknameTouched, setNicknameTouched] = useState(false);

  // UI / async state.
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // `unmountedRef` lets us avoid `setState` after unmount when a slow join
  // promise resolves after the component has gone away. React 18's strict
  // mode double-mounts components in dev, so this also keeps `useEffect`
  // cleanup honest.
  const unmountedRef = useRef(false);
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  // ---------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------

  const handlePasswordChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setPassword(e.target.value);
      // Don't clear the error on every keystroke — the error message stays
      // until the next submission so the user has time to read it. This
      // matches the WCAG guidance for status messages.
    },
    [],
  );

  const handleNicknameChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setNickname(e.target.value);
      setNicknameTouched(true);
    },
    [],
  );

  /**
   * On blur, compute the roomId and consult storage for a stored nickname.
   * Why blur instead of `onChange` debounce?
   *   - SHA-256 over short strings is cheap, but doing it on every
   *     keystroke is wasteful; blur is the natural "I'm done typing the
   *     password" signal.
   *   - It also avoids fighting the user mid-type by suddenly populating
   *     the nickname field while they're still typing the password.
   */
  const handlePasswordBlur = useCallback(async () => {
    if (password.length === 0) return;
    try {
      const roomId = await sha256Hex(password);
      if (unmountedRef.current) return;
      // Always consult storage so this side-effect is observable in tests
      // and so the lookup cost is paid up front (it's a synchronous
      // localStorage read). The *application* of the result is what we
      // gate on `nicknameTouched`: a user who has already typed a value
      // must not have it overwritten on blur (R9.2 implementation note).
      const stored = storage.loadNickname(roomId);
      if (nicknameTouched) return;
      if (stored !== null && stored.length > 0) {
        setNickname(stored);
        // Note: we deliberately don't flip `nicknameTouched` to true here.
        // If the user later types over the prefilled value, the change
        // handler will still treat it as a manual edit.
      }
    } catch (err) {
      // Storage / hash failures are non-fatal for the entry flow — they
      // just mean we couldn't prefill. Log for diagnostics and continue.
      console.warn('[PasswordEntry] prefill failed', err);
    }
  }, [password, nicknameTouched, sha256Hex, storage]);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (submitting) return;

      const clientError = validateInputs(password, nickname);
      if (clientError !== null) {
        setErrorMessage(clientError);
        return;
      }

      setSubmitting(true);
      setErrorMessage(null);

      try {
        const roomId = await sha256Hex(password);
        // The server recomputes the canonical SignalR userId from
        // `(roomId, nickname)` and ignores any `userId` field on this
        // request body, so we deliberately omit it. The `JoinRoomRequest`
        // type was made optional in task 7.1 (tasks.md Implementation
        // Notes 4.3-1 / 6.1).
        const result = await chatApi.join(roomId, { nickname });
        if (unmountedRef.current) return;

        if (result.ok) {
          // Persist BEFORE notifying the parent so a parent that
          // immediately unmounts us still benefits from the save.
          storage.saveNickname(roomId, nickname);
          onJoined({ roomId, nickname, joinOutput: result.value });
          // Intentionally do NOT clear `submitting` on success: the
          // parent typically unmounts this component, and leaving the
          // flag on prevents a flash of the re-enabled button.
          return;
        }

        setErrorMessage(describeError(result.error));
      } catch (err) {
        // SHA-256 should not throw in practice (Web Crypto is built into
        // modern browsers), but if it does we surface a generic message
        // and log the cause.
        console.error('[PasswordEntry] submit failed', err);
        if (!unmountedRef.current) {
          setErrorMessage('予期せぬエラーが発生しました。');
        }
      } finally {
        // Only re-enable the button if we are still mounted AND we did
        // not invoke `onJoined`. The success path returns early above.
        if (!unmountedRef.current) {
          setSubmitting(false);
        }
      }
    },
    [submitting, password, nickname, sha256Hex, chatApi, storage, onJoined],
  );

  // ---------------------------------------------------------------------
  // Render — UI 刷新 §6.1 に基づく PasswordEntry レイアウト
  // ---------------------------------------------------------------------

  const body = (
    <section
      className={embedded ? 'password-entry password-entry--embedded' : 'password-entry'}
      aria-label="ChocoMesse エントリー"
    >
      {showMenu && (
        <div className="password-entry__menu">
          <HamburgerMenu />
        </div>
      )}

      {/* 見出しはスタンドアロン表示時のみ。LandingPage 埋め込み時は
          ページ側のヒーロー見出しに一本化するため描画しない。 */}
      {!embedded && (
        <div className="password-entry__hero">
          {/* テーマ別の装飾ステッカー (mocha = washi tape / sage = orb) */}
          <span
            className="sticker sticker--washi"
            style={{ top: 10, right: 24, '--sticker-rotate': '6deg' } as React.CSSProperties}
            aria-hidden="true"
          >
            for two
          </span>

          <div className="password-entry__badge">
            <span className="password-entry__badge-icon" aria-hidden="true">🤍</span>
            <span className="password-entry__badge-text">ChocoMesse</span>
          </div>

          <h1 id="password-entry-title" className="password-entry__title">
            合言葉でつながる、
            <span className="password-entry__title-accent--mocha" data-decor="washi">
              two of us
            </span>
            <span className="password-entry__title-accent--sage" data-decor="orb">
              二人の世界
            </span>
          </h1>

          <p className="password-entry__lede">
            同じ合言葉とニックネームを共有して、<br />
            二人だけのチャットを始めましょう
          </p>
        </div>
      )}

      <form
        className="password-entry__card password-entry__form"
        onSubmit={handleSubmit}
        noValidate
      >
        <span
          className="sticker sticker--washi-rose password-entry__card-sticker"
          aria-hidden="true"
        >
          ♡
        </span>

        <div className="password-entry__field">
          <label
            htmlFor="password-entry-password"
            className="password-entry__label"
          >
            合言葉（パスワード）
          </label>
          <input
            id="password-entry-password"
            className="password-entry__input password-entry__input--password"
            type="password"
            value={password}
            onChange={handlePasswordChange}
            onBlur={handlePasswordBlur}
            autoComplete="off"
            spellCheck={false}
            disabled={submitting}
            placeholder="••••••••"
          />
        </div>

        <div className="password-entry__field">
          <label
            htmlFor="password-entry-nickname"
            className="password-entry__label"
          >
            ニックネーム
          </label>
          <input
            id="password-entry-nickname"
            className="password-entry__input"
            type="text"
            value={nickname}
            onChange={handleNicknameChange}
            autoComplete="off"
            maxLength={NICKNAME_MAX_LENGTH + 1 /* allow 33 to trigger validation */}
            disabled={submitting}
            placeholder="ニックネーム"
          />
        </div>

        <button
          type="submit"
          className="password-entry__submit"
          disabled={submitting}
        >
          {submitting ? '入室中…' : '入室する →'}
        </button>

        <div
          className="password-entry__error"
          role="alert"
          aria-live="assertive"
        >
          {errorMessage}
        </div>
      </form>

      <div className="password-entry__divider" aria-hidden="true">
        <span className="password-entry__divider-line" />
        <span>または</span>
        <span className="password-entry__divider-line" />
      </div>

      <button
        type="button"
        className="password-entry__login-link"
        onClick={onGoToLogin}
        disabled={submitting}
      >
        ユーザー登録・ログインして部屋を管理する
      </button>
    </section>
  );

  // 埋め込みモードでは LandingPage 側が背景・レイアウトを持つため、
  // スタンドアロン用の全画面ラッパー（theme-stage / 背景 blob）は描画しない。
  if (embedded) {
    return body;
  }

  return (
    <div className="theme-stage">
      <span className="theme-blob theme-blob--a" aria-hidden="true" />
      <span className="theme-blob theme-blob--b" aria-hidden="true" />
      <span className="theme-blob theme-blob--c" aria-hidden="true" />

      <div className="theme-stage__content">{body}</div>
    </div>
  );
}
